/** Tag → General tab. Basic fields: name, category, associated user, vehicle,
 *  and access tags. (The `data` blob is edited on the Data tab.)
 *
 *  The associated user and the vehicle are pickers, so their click belongs to
 *  the widget; the links live in a hover popover on the picked value rendered
 *  underneath (`tagLinks`). Extras that need the saved record — the user's
 *  email — only appear while the picked user IS the saved one. */
import { Box, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as fleetVehiclesApi from '@/api/fleetVehicles';
import { rbacApi, type AccessTag } from '@/api/rbac';
import * as tagsApi from '@/api/tags';
import type { TagCategoryBrief, TagDetail } from '@/api/tags';
import * as usersApi from '@/api/users';
import { EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { FieldHelp } from '@/components/FieldHelp';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';

import { TAG_HELP } from '../fieldHelp';
import { tagCategoryActions, tagUserActions, tagVehicleActions } from '../tagLinks';
import { TagCategoryPicker } from '../TagCategoryPicker';

interface Ref2 {
  id: number;
  label: string;
}

const ids = (xs: { id: number }[]) => xs.map((x) => x.id).sort((a, b) => a - b);
const sameIds = (a: { id: number }[], b: { id: number }[]) =>
  a.length === b.length && ids(a).every((v, i) => v === ids(b)[i]);

/** The picked value as a hover trigger, under the picker it belongs to. */
function PickedValue({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: -1.5 }}>
      {children}
    </Typography>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>{title}</Typography>
      {children}
    </Box>
  );
}

export function TagGeneralTab({ tag, onSaved }: { tag: TagDetail; onSaved: () => void }) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_tags_manage');
  const snackbar = useSnackbar();
  const tenantId = tag.tenant?.id ?? null;

  const [name, setName] = useState(tag.name);
  const [customId, setCustomId] = useState(tag.custom_id ?? '');
  const [category, setCategory] = useState<TagCategoryBrief | null>(tag.category);
  const [user, setUser] = useState<Ref2 | null>(
    tag.user ? { id: tag.user.id, label: tag.user.name || tag.user.email || `#${tag.user.id}` } : null,
  );
  const [vehicle, setVehicle] = useState<Ref2 | null>(
    tag.vehicle ? { id: tag.vehicle.id, label: tag.vehicle.name } : null,
  );
  const [atags, setAtags] = useState<AccessTag[]>(() => tag.atag.map((a) => ({ id: a.id, name: a.name ?? '' })));

  const resetToRecord = () => {
    setName(tag.name);
    setCustomId(tag.custom_id ?? '');
    setCategory(tag.category);
    setUser(tag.user ? { id: tag.user.id, label: tag.user.name || tag.user.email || `#${tag.user.id}` } : null);
    setVehicle(tag.vehicle ? { id: tag.vehicle.id, label: tag.vehicle.name } : null);
    setAtags(tag.atag.map((a) => ({ id: a.id, name: a.name ?? '' })));
  };
  useEffect(() => {
    resetToRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag]);

  const dirty = useMemo(() => {
    const atagRefs = tag.atag.map((a) => ({ id: a.id }));
    return (
      name.trim() !== tag.name ||
      (customId.trim() || null) !== (tag.custom_id ?? null) ||
      (category?.id ?? null) !== (tag.category?.id ?? null) ||
      (user?.id ?? null) !== (tag.user?.id ?? null) ||
      (vehicle?.id ?? null) !== (tag.vehicle?.id ?? null) ||
      !sameIds(atags, atagRefs)
    );
  }, [name, customId, category, user, vehicle, atags, tag]);

  const patch = useMutation({
    mutationFn: (body: tagsApi.TagWriteInput) => tagsApi.update(tag.id, body),
    onSuccess: () => {
      snackbar.show('Saved.', 'success');
      onSaved();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const save = () => {
    if (!name.trim() || !category) {
      snackbar.show('Name and category are required.', 'error');
      return;
    }
    patch.mutate({
      name: name.trim(),
      category: category.id,
      // `custom_id` is only accepted on an externally identified category, and
      // the field is only rendered there -- omit the key entirely otherwise so a
      // category switch does not smuggle a stale id along.
      ...(category.external_identity ? { custom_id: customId.trim() || null } : {}),
      user: user?.id ?? null,
      vehicle: vehicle?.id ?? null,
      atag: atags.map((a) => ({ id: a.id })),
    });
  };

  return (
    <Box>
      <Section title="Basics">
        <Stack spacing={2} sx={{ maxWidth: 640 }}>
          <Typography variant="body2" color="text.secondary">
            Tenant: {tag.tenant ? `${tag.tenant.name} (${tag.tenant.code})` : 'System'}
          </Typography>
          <TextField label="Name" size="small" fullWidth value={name} disabled={!canManage}
            onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 1024 }} />
          {canManage ? (
            <TagCategoryPicker value={category} onChange={setCategory} tourOperatorId={tenantId} />
          ) : (
            <Typography variant="body2">Category: {category ? `${category.name} (${category.code})` : '—'}</Typography>
          )}
          {canManage && category && (
            <PickedValue>
              <ValueActions actions={tagCategoryActions(category, { tenantId })}>
                {category.code}
              </ValueActions>
            </PickedValue>
          )}
          {/* On a name-identified category an External ID is not merely empty --
              the write path refuses one -- so the field is absent rather than
              disabled, and the reason is stated where the operator would look. */}
          {category?.external_identity ? (
            <TextField
              label="External ID"
              size="small"
              fullWidth
              value={customId}
              disabled={!canManage}
              onChange={(e) => setCustomId(e.target.value)}
              inputProps={{ maxLength: 128 }}
              helperText="The upstream entity this tag mirrors. Changing it re-points the tag at a different entity; clearing it returns the tag to name identity."
              InputProps={{ endAdornment: <FieldHelp help={TAG_HELP.custom_id} /> }}
            />
          ) : (
            tag.custom_id && (
              <Typography variant="caption" color="warning.main">
                External ID {tag.custom_id} is set, but this category identifies tags by name.
              </Typography>
            )
          )}
        </Stack>
      </Section>

      <Section title="Associations">
        <Stack spacing={2} sx={{ maxWidth: 640 }}>
          <RemoteAutocomplete<Ref2>
            label="Associated user" size="small" fetchOnEmpty value={user}
            getOptionLabel={(o) => o.label} getOptionKey={(o) => o.id}
            queryFn={async (q) =>
              (
                await usersApi.list({
                  search: q,
                  pageSize: 20,
                  ...(tenantId != null ? { tour_operator: tenantId } : {}),
                })
              ).results.map((u) => ({
                id: u.id,
                label: `${u.first_name} ${u.last_name}`.trim() || u.email || `#${u.id}`,
              }))
            }
            onChange={(v) => setUser((v as Ref2 | null) ?? null)}
          />
          {user && (
            <PickedValue>
              <ValueActions
                actions={tagUserActions(
                  {
                    id: user.id,
                    // The email is only known for the SAVED association.
                    email: user.id === tag.user?.id ? tag.user.email : null,
                    name: user.label,
                  },
                  { tenantId },
                )}
              >
                {user.label}
              </ValueActions>
            </PickedValue>
          )}
          <RemoteAutocomplete<Ref2>
            label="Vehicle" size="small" fetchOnEmpty value={vehicle}
            getOptionLabel={(o) => o.label} getOptionKey={(o) => o.id}
            queryFn={async (q) =>
              (await fleetVehiclesApi.list({ search: q, pageSize: 20, ...(tenantId != null ? { tour_operator_id: tenantId } : {}) })).results.map((v) => ({
                id: v.id,
                label: v.license_plate ? `${v.name} (${v.license_plate})` : v.name,
              }))
            }
            onChange={(v) => setVehicle((v as Ref2 | null) ?? null)}
          />
          {vehicle && (
            <PickedValue>
              <ValueActions actions={tagVehicleActions({ id: vehicle.id }, { tenantId })}>
                {vehicle.label}
              </ValueActions>
            </PickedValue>
          )}
        </Stack>
      </Section>

      {tenantId != null && (
        <Section title="Access">
          <Box sx={{ maxWidth: 640 }}>
            <RemoteAutocomplete<AccessTag>
              label="Access tags" size="small" multiple fetchOnEmpty value={atags}
              getOptionLabel={(o) => o.name} getOptionKey={(o) => o.id}
              queryFn={async (q) => (await rbacApi.listAccessTags(tenantId, q)).results}
              onChange={(v) => setAtags((v as AccessTag[]) ?? [])}
            />
          </Box>
        </Section>
      )}

      <EditorSaveBar
        dirty={dirty} saving={patch.isPending} canManage={canManage}
        onSave={save} onDiscard={resetToRecord}
      />
    </Box>
  );
}
